// Pure, client-safe helpers for the cached list pages. The server renders the
// whole product set for a view; the client slices and filters it from the URL
// (?page=, ?brand=, ?category=) so the routes stay static/ISR-cacheable.

import { brandSlug, listBrands } from "../../lib/brands";
import type { CatalogLite } from "../../lib/types";

export const PER_PAGE = 24;

export function parsePage(page?: string | null): number {
  return Math.max(1, Number(page) || 1);
}

/** Brand filter chips for a product set: only when it spans 2+ brands, biggest first. */
export function brandChipsFor(products: CatalogLite[]): { slug: string; label: string; count: number }[] {
  const brands = listBrands(products).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return brands.length >= 2 ? brands : [];
}

/** Narrow a product set to one brand slug; an unknown slug leaves the set whole. */
export function filterByBrand(products: CatalogLite[], brand?: string | null): { products: CatalogLite[]; active?: string } {
  if (!brand) return { products };
  const hit = products.filter((p) => brandSlug(p.brand) === brand);
  return hit.length > 0 ? { products: hit, active: brand } : { products };
}

/** One page of a list, with the page number clamped into range. */
export function paginate<T>(list: T[], page?: string | null, perPage = PER_PAGE) {
  const totalPages = Math.max(1, Math.ceil(list.length / perPage));
  const current = Math.min(parsePage(page), totalPages);
  return { items: list.slice((current - 1) * perPage, current * perPage), current, totalPages };
}
